import {
  Button,
  Checkbox,
  DialogV2,
  Dropdown,
  DropdownRef,
  Input,
  Loader,
  Menu,
  Pill,
  Typography,
} from '@recruit-robin/robin-theme/web-components';
import { ChevronDown, ChevronUp, Edit_05 } from '@recruit-robin/robin-theme/web-icons';
import { useCallback, useContext, useRef, useState } from 'react';
import { useDebounce } from 'react-use';

import { NylasCalendarDontShowAgain } from '@/features/nylas-calendar-manage-connection-modal';
import type { Vacancy } from 'clients/AutoCompleteClient/types';

import { CreateJob } from 'components/ui/molecules/VacancyList/components/CreateJob/CreateJob';
import { VacancyList } from 'components/ui/molecules/VacancyList/VacancyList';
import { EnrichVacancyModalType } from 'shared/legacy/contexts/DialogContext/modals/EnrichVacancyModal/components/EnrichVacancyModalContext/EnrichVacancyModalContext.types';
import { useGTM } from 'hooks/gtm';
import { useEnrichedVacanciesQuery } from 'hooks/reachout/useEnrichedVacanciesQuery';
import { EnrichVacancyEvent } from 'services/event-bus/EventBus.types';
import { useEvent } from 'services/event-bus/useEvent';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useIsIndeedFlow } from 'shared/legacy/contexts/CommAgentFlowContext';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import {
  getVacancyInfoLevel,
  getVacancyName,
  useVacancyInfoSufficiencyConfig,
  VacancyInfoLevel,
} from 'utils/vacancyEnrichedUtils';
import { useStyles } from './ActivateRobinJrDialog.styles';
import { ActivateRobinJrDialogProps, ActivateRobinJrDialogType } from './ActivateRobinJrDialog.types';
import { useDialogConfig } from './ActivateRobinJrDialog.useConfig';
import { IndeedActivationSection } from './IndeedActivationSection';
import { getIndeedActivationState } from './IndeedActivationSection.utils';

export const ActivateRobinJrDialog = ({
  handleOutreachModalRequestClose,
  onConfirmSelectedVacancy,
  isLoading,
  selectedAts,
  setSelectedAts,
  isProcessingVacancy,
  type,
  dontShowMessagePreview = false,
  setDontShowMessagePreview,
  showMessagePreviewCheckbox = false,
  dontShowWhatsAppMessagePreview = false,
  setDontShowWhatsAppMessagePreview,
  showWhatsAppPreviewCheckbox = false,
  dontShowQuestionnairePreview = false,
  setDontShowQuestionnairePreview,
  showQuestionnairePreviewCheckbox = false,
  showNylasCalendarConnectionCheckbox = false,
  selectedIndeedProject,
  setSelectedIndeedProject,
}: ActivateRobinJrDialogProps) => {
  const classes = useStyles({});
  const { dictionary } = useContext(LocalizationContext);
  const isIndeedFlow = useIsIndeedFlow();

  const dropdownRef = useRef<DropdownRef>(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState(search);
  useDebounce(() => setSearchDebounced(search), 230, [search]);
  const config = useVacancyInfoSufficiencyConfig();
  const enrichedVacanciesQuery = useEnrichedVacanciesQuery({ search: searchDebounced });
  const { data: atsVacancyList = [], isFetching } = enrichedVacanciesQuery;
  const enrichVacancyModal = useGlobalDialog(GlobalDialogType.EnrichVacancy);
  const gtm = useGTM();

  useEvent(
    EnrichVacancyEvent.Selected,
    ({ vacancy }) => {
      if (selectedAts?.db_id && vacancy.db_id === selectedAts.db_id) {
        setSelectedAts(vacancy);
      }
      enrichedVacanciesQuery.refetch();
    },
    [enrichedVacanciesQuery, selectedAts, setSelectedAts],
  );

  useEvent(
    EnrichVacancyEvent.Deleted,
    ({ vacancyId }) => {
      if (selectedAts?.db_id === vacancyId) {
        setSelectedAts(null);
      }
      enrichedVacanciesQuery.refetch();
    },
    [enrichedVacanciesQuery, selectedAts, setSelectedAts],
  );

  const isSearchLoading = isFetching || search !== searchDebounced;

  const {
    title,
    iconTitle: IconTitle,
    description,
    confirmButtonLabel,
    footerMessage,
    footerMessageColor,
    footerIcon: FooterIcon,
  } = useDialogConfig(type);

  const getFooterColor = (c: any) => {
    const [family, variant, shade] = footerMessageColor.split('.');
    return c[family][variant][shade];
  };

  const handleEdit = useCallback(
    (vacancy: Vacancy) => {
      if (!vacancy.is_improved) {
        gtm.clickOnEnrichedJob();
      }
      enrichVacancyModal.open({ payload: { vacancy } });
    },
    [enrichVacancyModal, gtm],
  );

  const handleCreateJob = useCallback(() => {
    enrichVacancyModal.open({
      payload: { modalType: EnrichVacancyModalType.Ats },
    });
  }, [enrichVacancyModal]);

  const endIcon = useCallback(
    (isOpen: boolean) => {
      return (
        <div css={classes.endIcon}>
          {selectedAts?.quality_percent && (
            <Pill
              label={config[getVacancyInfoLevel(selectedAts.quality_percent)].pill.label}
              style="icon-removable"
              type={config[getVacancyInfoLevel(selectedAts.quality_percent)].pill.type}
              size="small"
              onClick={() => undefined}
              padding="text"
            />
          )}
          {selectedAts && (
            <div
              css={classes.editIconButton}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleEdit(selectedAts);
              }}>
              <Edit_05 size={16} color={(c) => c.neutral.variant[60]} />
            </div>
          )}
          {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      );
    },
    [selectedAts, handleEdit, classes.endIcon, classes.editIconButton, config],
  );

  const handleSelect = (ats: Vacancy | null) => {
    setSelectedAts(ats);
    dropdownRef.current?.close();
  };

  const qualityLevel = getVacancyInfoLevel(selectedAts?.quality_percent ?? 0);
  const isQualityHigh = qualityLevel === VacancyInfoLevel.HIGH;
  const indeedActivationState = getIndeedActivationState({
    isLoading,
    isProcessingVacancy,
    selectedIndeedProject,
    selectedVacancy: selectedAts,
  });

  const isConfirmDisabled = isIndeedFlow
    ? indeedActivationState.isConfirmDisabled
    : !selectedAts || !isQualityHigh || isProcessingVacancy || isLoading;

  return (
    <DialogV2.Root isVisible customCss={classes.dialog}>
      <DialogV2.Backdrop />
      <DialogV2.Content customCss={classes.content}>
        <DialogV2.Header customCss={classes.title}>
          <IconTitle size={24} color={(c) => c.primary.fuchsia[40]} />
          <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
            {title}
          </Typography>
        </DialogV2.Header>

        <DialogV2.Body>
          <div css={classes.body}>
            {isIndeedFlow ? (
              <IndeedActivationSection
                selectedVacancy={selectedAts}
                onEditVacancy={handleEdit}
                setSelectedVacancy={setSelectedAts}
                isProcessingVacancy={isProcessingVacancy}
                selectedIndeedProject={selectedIndeedProject}
                setSelectedIndeedProject={setSelectedIndeedProject}
              />
            ) : (
              <>
                <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
                  {description}
                </Typography>

                <Dropdown
                  placement="bottom"
                  flipMenu={false}
                  ref={dropdownRef}
                  css={classes.selectWrapper}
                  renderMenu={
                    <Menu.Root customCss={classes.select}>
                      <div css={classes.createJobWrapper}>
                        <CreateJob
                          customCss={classes.createJob}
                          onCreateJob={handleCreateJob}
                          selectedVacancy={selectedAts}
                        />
                      </div>
                      {isSearchLoading ? (
                        Array.from({ length: 4 }).map((_, i) => <Menu.Item.Skeleton key={i} />)
                      ) : (
                        <VacancyList
                          onCreateJob={handleCreateJob}
                          selectedVacancy={selectedAts}
                          onSelectVacancy={handleSelect}
                          onEditVacancy={handleEdit}
                          vacanciesList={atsVacancyList}
                          showEnrichedVacancyButton
                        />
                      )}
                    </Menu.Root>
                  }>
                  {({ isOpen }) => (
                    <Input
                      endIcon={endIcon(isOpen)}
                      onInput={(e) => setSearch(e.currentTarget.value)}
                      placeholder={dictionary.searchVacancy}
                      value={isOpen ? search : selectedAts ? getVacancyName(selectedAts) : dictionary.searchVacancy}
                      contained
                    />
                  )}
                </Dropdown>
              </>
            )}

            <div css={classes.footerMessageContainer}>
              {FooterIcon && <FooterIcon size={16} color={getFooterColor} />}
              <Typography variant={(v) => v.body.short} color={getFooterColor}>
                {footerMessage}
              </Typography>
            </div>

            {showMessagePreviewCheckbox && type === ActivateRobinJrDialogType.Activate && (
              <Checkbox
                label={dictionary.dontShowMessagePreviewNextTime}
                checked={dontShowMessagePreview ?? false}
                onClick={(value) => setDontShowMessagePreview(value)}
              />
            )}

            {showWhatsAppPreviewCheckbox &&
              type === ActivateRobinJrDialogType.Activate &&
              setDontShowWhatsAppMessagePreview && (
                <Checkbox
                  label={dictionary.dontShowWhatsappMessagePreviewNextTime}
                  checked={dontShowWhatsAppMessagePreview ?? false}
                  onClick={(value) => setDontShowWhatsAppMessagePreview(value)}
                />
              )}

            {showQuestionnairePreviewCheckbox && type === ActivateRobinJrDialogType.Activate && (
              <Checkbox
                label={dictionary.dontShowQualificationQuestionaryPreviewNextTime}
                checked={dontShowQuestionnairePreview ?? false}
                onClick={(value) => setDontShowQuestionnairePreview(value)}
              />
            )}

            {showNylasCalendarConnectionCheckbox && type === ActivateRobinJrDialogType.Activate && (
              <NylasCalendarDontShowAgain label={dictionary.dontShowSetupInterviewAgain} />
            )}
          </div>
        </DialogV2.Body>

        {!isProcessingVacancy && (
          <DialogV2.Footer>
            <Button label={dictionary.cancel} onClick={handleOutreachModalRequestClose} style="text" type="subtle2" />
            <Button
              label={confirmButtonLabel}
              onClick={onConfirmSelectedVacancy}
              style="filled"
              type="success"
              disabled={isConfirmDisabled}
              startIcon={isLoading ? <Loader size={16} /> : undefined}
            />
          </DialogV2.Footer>
        )}
      </DialogV2.Content>
    </DialogV2.Root>
  );
};
